import { appendToHead, stringToElements, getTheme } from '../helpers/index.js';
import { UDS_THEME } from '../global-constants.js';

export const getPricingFacetCSS = (isLightTheme) => {
	const imagePath = '/static/assets/images/icons/my-wallet';
	const myWalletLogoColor = isLightTheme ? 'black' : 'white';
	const myWalletLogoUrl = `${imagePath}/${myWalletLogoColor}.svg`;

	const cssTemplate = `
		<style type="text/css">
			.mywallet-icon {
				background-image: url('${myWalletLogoUrl}');
				background-size: contain;
			}
			.mywallet-icon{
				width: 18px;
				height: 18px;
				display: inline-block;
				margin: 0 4px -4px 0;
			}
			.btn-my-wallet-integration .mywallet-icon{
				background-image: url('${imagePath}/white.svg');
			}
			.btn-my-wallet-integration.dark .mywallet-icon{
				background-image: url('${imagePath}/black.svg');
			}
			.ddc-site .mycars-default, 
			.ddc-site .header-mycars-search-container{
				display: flex;
				align-items: center;
			}
			.ddc-site .mycars-default [data-web-api-id='mywallet'].navbar{
				min-height: auto;
			}
			.ddc-site .noShrink .ws-mycars-nav .menu.wallet-link > a{
				display: flex;
				align-items: center;
			}
			.ddc-site .mycars-default [data-location='header-toolbar']{
				display: flex;
			}
			.ddc-site .mycars-default [data-location='header-toolbar'] .navbar{
				margin: 0;
			}
			.ddc-site .mycars-default [data-location='header-toolbar'] .mycars.mycars-integration-navbar{
				height: auto;
			}
			.page-header .minimal .navbar-default [data-location='header-toolbar'].btn.ddc-btn-nav{
				display: inline-flex;
			}
			.wallet-link a{
				text-decoration-color: transparent;
			}
			.wallet-link a:before{
				font-family: ddc-icons;
				speak: none;
				font-style: normal;
				font-weight: 400;
				font-variant: normal;
				text-transform: none;
				-webkit-font-smoothing: antialiased;
			}
		</style>
	`;

	return stringToElements(cssTemplate);
}

// NOTE This is not currently in use, but doesn't add to the size of the build.
// We can remove it if we decide we don't need loading indicators (or can use another approach).
export const getLoadingIndicatorCSS = () => {
	const cssTemplate = `
		<style type="text/css">
			.lds-ellipsis {
				display: inline-block;
				position: absolute;
				width: 30px;
				height: 10px;
			}
			.lds-ellipsis div {
				position: absolute;
				top: 0;
				width: 8px;
				height: 8px;
				border-radius: 50%;
				background: var(--color-neutral-500);
				animation-timing-function: cubic-bezier(0, 1, 1, 0);
			}
			.lds-ellipsis div.one {
				left: 8px;
				animation: lds-ellipsis1 0.6s infinite;
			}
			.lds-ellipsis div.two {
				left: 8px;
				animation: lds-ellipsis2 0.6s infinite;
			}
			.lds-ellipsis div.three {
				left: 32px;
				animation: lds-ellipsis2 0.6s infinite;
			}
			.lds-ellipsis div.four {
				left: 56px;
				animation: lds-ellipsis3 0.6s infinite;
			}
			@keyframes lds-ellipsis1 {
				0% {
					transform: scale(0);
				}
				100% {
					transform: scale(1);
				}
			}
			@keyframes lds-ellipsis3 {
				0% {
					transform: scale(1);
				}
				100% {
					transform: scale(0);
				}
			}
			@keyframes lds-ellipsis2 {
				0% {
					transform: translate(0, 0);
				}
				100% {
					transform: translate(24px, 0);
				}
			}
		</style>
	`;
	return stringToElements(cssTemplate);
}

/**
 * This function sets custom properties on the root element for the wallet and UDS theming.
 * Where applicable, we're mapping base properties to DDC properties.
 * This could change/update in the future when DDC (CBDP) has a more robust theming system.
 * @param {pageData} Object 
 */
const setRootCustomProperties = (pageData) => {
	const { layoutType } = pageData;
	const theme = getTheme(pageData);
	// Get DDC font family and weight
	const bodyElement = document.querySelector('body');
	const bodyComputedStyle = window.getComputedStyle(bodyElement);
	const ddcBodyFontFamily = bodyComputedStyle.fontFamily;
	const ddcBodyFontSize = bodyComputedStyle.fontSize;
	const headingElement = document.querySelector('h3');
	const ddcHeadingFontFamily = headingElement ? window.getComputedStyle(headingElement).fontFamily : ddcBodyFontFamily
	
	// Apply DDC font family to the variable
	const root = document.querySelector(':root');
	root.style.setProperty('--base-type-font-heading', ddcHeadingFontFamily);
	root.style.setProperty('--base-type-font-body', ddcBodyFontFamily);
	root.style.setProperty('--base-type-size-md', ddcBodyFontSize);

	// custom colors (temp until we work into design system)
	root.style.setProperty('--custom-color-form-input-radio-background', '#2C78F6');

	// general base colors
	root.style.setProperty('--base-color-white', 'var(--color-neutral-0)');
	root.style.setProperty('--base-color-error-700','var(--color-danger-500)');
	root.style.setProperty('--base-color-neutral-100', 'var(--color-neutral-100)');
	root.style.setProperty('--base-color-neutral-200', 'var(--color-neutral-200)');
	root.style.setProperty('--base-color-neutral-300', 'var(--color-neutral-300');
	root.style.setProperty('--base-color-neutral-400', 'var(--color-neutral-400)');
	root.style.setProperty('--base-color-neutral-600', 'var(--color-neutral-600)');
	root.style.setProperty('--base-color-neutral-800', 'var(--color-neutral-800)');
	root.style.setProperty('--base-color-neutral-900','var(--color-neutral-900)');
	root.style.setProperty('--base-color-primary-500', 'var(--color-primary-500)');
	root.style.setProperty('--base-color-primary-600', 'var(--color-primary-600)');
	root.style.setProperty('--base-color-primary-700', 'var(--color-primary-700)');
	root.style.setProperty('--base-color-primary-800', 'var(--color-primary-800)');
	root.style.setProperty('--base-color-secondary-500', 'var(--color-primary-800)');
	root.style.setProperty('--base-size-border-sm', '1px');

	// General component level specific properties
	root.style.setProperty('--comp-color-background-selectable-tabs-icon','var(--color-primary-500)');
	root.style.setProperty('--comp-color-border-input-hover','var(--input-border-color)');
	root.style.setProperty('--comp-color-fill-spot-icon-secondary','var(--color-primary-500)');
	root.style.setProperty('--comp-size-radius-input','var(--input-border-radius-base)');
	root.style.setProperty('--comp-size-radius-button', 'var(--btn-border-radius-base)');

	if(theme === UDS_THEME.DARK) {
		root.style.setProperty('--comp-color-content-link-hover','var(--base-color-primary-600, #024aa5)');
		root.style.setProperty('--comp-color-content-link-rest', 'var(--base-color-primary-700, #0f3485)');
		root.style.setProperty('--comp-color-content-link-pressed', 'var(--base-color-primary-800, #012169)');
		root.style.setProperty('--comp-color-content-label-type-info-inverse','var(--base-color-primary-800, #012169)');
		root.style.setProperty('--comp-color-background-label-body-info-inverse','var(--base-color-primary-100, #e3f2ff)');
	} else {
		root.style.setProperty('--comp-color-content-link-hover','var(--link-color)');
		root.style.setProperty('--comp-color-content-link-rest', 'var(--link-color)');
		root.style.setProperty('--comp-color-content-link-pressed', 'var(--link-color)');
		root.style.setProperty('--comp-color-content-label-type-info-inverse','var(--text-color)');
		root.style.setProperty('--comp-color-background-label-body-info-inverse','var(--color-neutral-0)');
	}

	// Primary button properties
	root.style.setProperty('--comp-color-background-button-primary-rest','var(--btn-primary-bg)');
	root.style.setProperty('--comp-color-background-button-primary-hover','var(--btn-primary-hover-bg)');
	root.style.setProperty('--comp-color-content-button-primary-rest','var(--btn-primary-color)');
	root.style.setProperty('--comp-color-content-button-primary-hover','var(--btn-primary-hover-color)');
	root.style.setProperty('--comp-color-border-button-primary-rest','var(--btn-primary-border-color)');
	root.style.setProperty('--comp-color-border-button-primary-hover','var(--btn-primary-hover-border-color)');
	// Secondary button properties
	root.style.setProperty('--comp-color-background-button-secondary-rest','var(--btn-default-bg)');
	root.style.setProperty('--comp-color-background-button-secondary-hover','var(--btn-default-hover-bg)');
	root.style.setProperty('--comp-color-content-button-secondary-rest','var(--btn-default-color)');
	root.style.setProperty('--comp-color-content-button-secondary-hover','var(--btn-default-hover-color)');
	root.style.setProperty('--comp-color-border-button-secondary-rest', 'var(--btn-default-border-color)');
	root.style.setProperty('--comp-color-border-button-secondary-hover','var(--btn-default-hover-border-color)');
	// Tertiary button properties
	root.style.setProperty('--comp-color-background-button-tertiary-rest','var(--btn-default-inverse-bg)');
	root.style.setProperty('--comp-color-background-button-tertiary-hover','var(--btn-default-inverse-hover-bg)');
	root.style.setProperty('--comp-color-content-button-tertiary-rest','var(--btn-default-inverse-color)');
	root.style.setProperty('--comp-color-content-button-tertiary-hover','var(--btn-default-inverse-hover-color)');
	root.style.setProperty('--comp-color-border-button-tertiary-rest','var(--btn-default-inverse-border-color)');
	root.style.setProperty('--comp-color-border-button-tertiary-hover','var(--btn-default-inverse-hover-border-color)');
	
	// base font sizes and component specific font sizes
	if (layoutType === 'mobile') {
		root.style.setProperty('--base-type-size-xxs', '10px');
		root.style.setProperty('--base-type-size-xs', '12px');
		root.style.setProperty('--base-type-size-sm', '14px');
		
		root.style.setProperty('--base-type-size-lg', '18px');
		root.style.setProperty('--base-type-size-xxl', '24px');
		root.style.setProperty('--base-type-size-3xl', '28px');
		root.style.setProperty('--base-type-size-4xl', '32px');
		root.style.setProperty('--base-type-size-5xl', '40px');
		root.style.setProperty('--base-type-size-6xl', '48px');
		// component specific font sizes where we could not update using base font sizes
		root.style.setProperty('--comp-type-size-select-button-label-hover', '1.75rem');
		root.style.setProperty('--comp-type-size-select-button-label-disabled', '1.2rem');
		root.style.setProperty('--comp-type-size-select-button-label-selected', '1.2rem');
		root.style.setProperty('--comp-type-size-select-button-value-disabled', '1.75rem');
		root.style.setProperty('--comp-type-size-select-button-value-selected', '1.75rem');
		
		// component level specific properties
		root.style.setProperty('--comp-type-size-button-md', 'var(--base-type-size-md)');

		// sem type sizes (for places where we could not update using base font sizes)
		root.style.setProperty('--sem-type-subheading-1','1.4rem');
	} else {
		root.style.setProperty('--base-type-size-xxs','0.75rem');
		root.style.setProperty('--base-type-size-xs', '12px');
		root.style.setProperty('--base-type-size-sm', '14px');
		root.style.setProperty('--base-type-size-xxl', '1.65rem');
		// component specific font sizes where we could not update using base font sizes
		root.style.setProperty('--comp-type-size-select-button-value-disabled', '1.1rem');
		root.style.setProperty('--comp-type-size-select-button-label', '1.1rem');
	}
}

/**
 * Load CSS for MyWallet integration
 * @param {Object} config - Configuration object
 * @param {Object} pageData - Page data object with layoutType, design, etc.
 */
export const loadCSS = (config, pageData) => {
	// Loading indicator we can use if desired. Commented out for now as I'm not sure if we need it.
	// const css = getLoadingIndicatorCSS();
	// appendToHead(css);
	if (config?.showFacetsPlacement && pageData?.design?.themekit) {
		const isLightTheme = getTheme(pageData) === UDS_THEME.LIGHT;
		const css = getPricingFacetCSS(isLightTheme);
		appendToHead(css);
	}

	// Apply wallet and UDS theming
	if (config?.testVariationTheming) {
		setRootCustomProperties(pageData);
	}
}
